'use client';

import { signOut, useSession } from 'next-auth/react';

// Third Party
import * as React from 'react';
import { Menu, Transition } from '@headlessui/react';
import classNames from 'classnames';

// Utils
import { routes } from '@routes';

// UI
import { Avatar } from '@components/avatar';
import { Icon, IconNames } from '@components/icon';
import { Link } from '@components/link';
import Image from 'next/image';

export const StaticNavigation: React.FC = () => {
  const { data: session } = useSession();

  const authenticatedUserNavigationDesktop: {
    name: string;
    href: VoidFunction | string;
    newWindow?: boolean;
  }[][] = [
    [
      { name: 'Account', href: routes.userAccountSettings },
      { name: 'Trips', href: routes.trips },
      { name: 'Favorites', href: routes.userAccountFavorites },
    ],
    [
      { name: 'Gift Cards', href: routes.giftCardPurchase, newWindow: true },
      { name: 'Help', href: routes.help, newWindow: true },
      {
        name: 'Log out',
        href: () => {
          signOut({ callbackUrl: '/', redirect: true });
        },
      },
    ],
  ];

  const authenticatedUserNavigationMobile: {
    name: string;
    href: VoidFunction | string;
    newWindow?: boolean;
  }[][] = [
    [
      { name: 'Account', href: routes.userAccountSettings },
      { name: 'Trips', href: routes.trips },
      { name: 'Favorites', href: routes.userAccountFavorites },
      { name: 'Manage Your Properties', href: routes.accountListings },
    ],
    [
      { name: 'Gift Cards', href: routes.giftCardPurchase, newWindow: true },
      { name: 'Help', href: routes.help, newWindow: true },
      {
        name: 'Log out',
        href: () => {
          signOut({ callbackUrl: '/', redirect: true });
        },
      },
    ],
  ];

  const unauthenticatedUserNavigationDesktop: {
    name: string;
    href: VoidFunction | string;
    newWindow?: boolean;
  }[][] = [
    [
      { name: 'Traveler Signup', href: routes.middleman },
      { name: 'Log In', href: routes.login },
    ],
    [
      { name: 'Gift Cards', href: routes.giftCardPurchase, newWindow: true },
      { name: 'Help', href: routes.help, newWindow: true },
    ],
  ];

  const unauthenticatedUserNavigationMobile: {
    name: string;
    href: VoidFunction | string;
    newWindow?: boolean;
  }[][] = [
    [
      { name: 'Traveler Signup', href: routes.middleman },
      { name: 'Log In', href: routes.login },
      { name: 'List Your Properties', href: routes.list },
    ],
    [
      { name: 'Gift Cards', href: routes.giftCardPurchase, newWindow: true },
      { name: 'Help', href: routes.help, newWindow: true },
    ],
  ];

  const desktopNavSections = (userLoggedIn: boolean) => {
    if (userLoggedIn) {
      return authenticatedUserNavigationDesktop;
    } else {
      return unauthenticatedUserNavigationDesktop;
    }
  };

  const mobileNavSections = (userLoggedIn: boolean) => {
    if (userLoggedIn) {
      return authenticatedUserNavigationMobile;
    } else {
      return unauthenticatedUserNavigationMobile;
    }
  };

  function checkNavType(
    name: string,
    active: boolean,
    href?: VoidFunction | string,
    newWindow?: boolean
  ) {
    if (typeof href === 'function') {
      return (
        <div key={name}>
          <button
            onClick={href}
            className={classNames(
              active ? 'bg-gray-xlight' : '',
              'block py-3 px-6 text-sm text-primary-main w-full text-start'
            )}
          >
            {name}
          </button>
        </div>
      );
    } else if (typeof href === 'string') {
      return (
        <div key={name}>
          {!newWindow && (
            <a
              href={href}
              className={classNames(
                active ? 'bg-gray-xlight' : '',
                'block py-3 px-6 text-sm text-primary-main'
              )}
            >
              {name}
            </a>
          )}
          {newWindow && (
            <a
              href={href}
              className={classNames(
                active ? 'bg-gray-xlight' : '',
                'block py-3 px-6 text-sm text-primary-main'
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {name}
            </a>
          )}
        </div>
      );
    }
    return (
      <div key={name}>
        <span
          className={classNames('block py-3 px-6 text-sm text-primary-main')}
        >
          {name}
        </span>
      </div>
    );
  }

  return (
    <div className="body-container">
      <div className="py-6">
        <div className="relative flex items-center justify-between">
          <Link to={routes.home}>
            <Image
              src="/assets/svg/logos/savvy_wordmark_slate.svg"
              className="block h-10 w-auto"
              alt="logo"
              width={100}
              height={100}
            />
          </Link>

          <div className="hidden sm:flex">{/* Center column */}</div>

          <div className="flex items-center justify-end">
            {session?.user && session.user.isHost ? (
              <Link
                to={routes.accountListings}
                className="ml-4 hidden text-sm md:inline-block"
              >
                Manage your Properties
              </Link>
            ) : (
              <Link
                to={routes.list}
                className="ml-4 hidden text-sm md:inline-block"
              >
                List your Properties
              </Link>
            )}

            {/* Desktop Nav Sections */}
            <Menu
              as="div"
              className="relative z-50 ml-5 flex-shrink-0 hidden sm:block"
            >
              <div>
                {/* @ts-ignore */}
                <Menu.Button className="flex items-center rounded-full bg-white hover:shadow-lg">
                  <span className="sr-only">Open user menu</span>
                  <div className="flex items-center rounded-full border-[1.5px] border-primary-main py-1 pl-4 pr-1">
                    <Icon name={IconNames.BARS} className="mr-4" />
                    <div className="w-8 h-8">
                      <Avatar
                        firstName={session?.user?.firstName}
                        lastName={session?.user?.lastName}
                        avatarUrl={session?.user?.avatarUrl}
                        shape="circle"
                      />
                    </div>
                  </div>
                </Menu.Button>
              </div>
              {/* @ts-ignore */}
              <Transition
                as="div"
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                {/* @ts-ignore */}
                <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right divide-y divide-primary-main rounded-md border-[1.5px] border-primary-main bg-white shadow-lg focus:outline-none">
                  {desktopNavSections(!!session).map((section, index) => {
                    return (
                      <div key={index} className="py-2">
                        {section.map((item) => (
                          // @ts-ignore
                          <Menu.Item key={item.name}>
                            {({ active }) =>
                              checkNavType(
                                item.name,
                                active,
                                item.href,
                                item.newWindow
                              )
                            }
                          </Menu.Item>
                        ))}
                      </div>
                    );
                  })}
                </Menu.Items>
              </Transition>
            </Menu>

            {/* Mobile Nav Sections */}
            <Menu
              as="div"
              className="flex-shrink-0 z-50 relative ml-5 block sm:hidden"
            >
              <div>
                {/* @ts-ignore */}
                <Menu.Button className="flex items-center rounded-full bg-white hover:shadow-lg">
                  <span className="sr-only">Open user menu</span>
                  <div className="flex items-center rounded-full border-[1.5px] border-primary-main py-1 pl-4 pr-1">
                    <Icon name={IconNames.BARS} className="mr-4" />
                    <div className="w-8 h-8">
                      <Avatar
                        firstName={session?.user?.firstName}
                        lastName={session?.user?.lastName}
                        avatarUrl={session?.user?.avatarUrl}
                        shape="circle"
                      />
                    </div>
                  </div>
                </Menu.Button>
              </div>
              {/* @ts-ignore */}
              <Transition
                as="div"
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                {/* @ts-ignore */}
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-light focus:outline-none">
                  {mobileNavSections(!!session).map((section, index) => {
                    return (
                      <div key={index} className="py-2">
                        {section.map((item) => (
                          <Menu.Item key={item.name}>
                            {({ active }: { active: boolean }) =>
                              checkNavType(
                                item.name,
                                active,
                                item.href,
                                item.newWindow
                              )
                            }
                          </Menu.Item>
                        ))}
                      </div>
                    );
                  })}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};
